import { MemberWithholdingStatus } from "@wingspanhq/users/dist/lib/interfaces/member";
import { useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useQueryInternalAccounts } from "../../../query/banking/queries/useQueryInternalAccounts";
import { useUserId } from "../../../query/hooks/helpers";
import { useUpdateMemberProfile } from "../../../query/users/mutations";
import { useMemberProfile } from "../../../query/users/queries";
import { selectorTaxWithholdingAccount } from "../../../shared/selectors/selectorTaxWithholdingAccount";
import { PATH_ONBOARDING_TAX_WITHHOLDINGS } from "../../Onboarding";
import { useModalTurnOn } from "../modals/ModalTurnOn";
import { PATH_TAXES_SURVEY } from "../paths";

export function useTaxWithholdingsStatus() {
  const history = useHistory();

  const userId = useUserId();
  const queryMember = useMemberProfile(userId);
  const queryInternalAccounts = useQueryInternalAccounts();
  const [update, updateMeta] = useUpdateMemberProfile();

  const modalTurnOn = useModalTurnOn();

  const isActive = useMemo(
    () =>
      queryMember.data?.profile.withholdings?.tax?.status ===
      MemberWithholdingStatus.Active,
    [queryMember.data?.profile.withholdings?.tax?.status]
  );

  const rate = useMemo(
    () => queryMember.data?.profile.withholdings?.tax?.rate,
    [queryMember.data?.profile.withholdings?.tax?.rate]
  );

  const taxWithholdingAccount = useMemo(() => {
    return queryInternalAccounts.data?.find(selectorTaxWithholdingAccount);
  }, [queryInternalAccounts.data]);

  const shouldCompleteSetup = useMemo(
    () => !taxWithholdingAccount || !rate,
    [taxWithholdingAccount, rate]
  );

  const turnOn = useCallback(async () => {
    if (shouldCompleteSetup) {
      history.push(PATH_ONBOARDING_TAX_WITHHOLDINGS);
      return;
    }

    modalTurnOn.open({
      onClose: result => {
        if (!result) {
          return;
        }

        update({
          memberId: userId,
          profile: {
            withholdings: {
              tax: {
                status: MemberWithholdingStatus.Active
              }
            }
          }
        });
      }
    });
  }, [history, modalTurnOn, shouldCompleteSetup, update, userId]);

  const turnOff = useCallback(() => {
    update(
      {
        memberId: userId,
        profile: {
          withholdings: {
            tax: {
              status: MemberWithholdingStatus.Paused
            }
          }
        }
      },
      {
        onSuccess: () => {
          history.push(PATH_TAXES_SURVEY);
        }
      }
    );
  }, [history, update, userId]);

  return {
    isActive,
    rate,
    taxWithholdingAccount,
    shouldCompleteSetup,
    updateMeta,
    turnOn,
    turnOff
  };
}
